/**
 * Offline Database using IndexedDB
 * Provides local storage for offline POS functionality.
 *
 * Every record body is AES-GCM encrypted at rest via `offlineCrypto.js`
 * before it touches IndexedDB — only the fields IndexedDB needs for keys/
 * indexes (product_id, phone, local_id, status, created_at, offline_id)
 * stay in plaintext. See offlineCrypto.js for key management.
 */
import { encryptValue, decryptValue } from './offlineCrypto';
import { getOfflineBranchId, OFFLINE_CACHE_VERSION } from './offlineScope';

const DB_NAME = 'pharmxai_offline';
const DB_VERSION = 4;

// Store names
const STORES = {
  PRODUCTS: 'products',
  SALES_QUEUE: 'sales_queue',
  SETTINGS: 'settings',
  SYNC_LOG: 'sync_log',
  RECENT_CUSTOMERS: 'recent_customers',
  REFILL_SCHEDULE: 'refill_schedule',
  PRODUCT_QUEUE: 'product_queue',
  PURCHASE_QUEUE: 'purchase_queue',
  ADR_QUEUE: 'adr_queue',
};

let db = null;

/**
 * Initialize IndexedDB
 */
export const initOfflineDB = () => {
  return new Promise((resolve, reject) => {
    if (db) {
      resolve(db);
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      console.error('IndexedDB error:', request.error);
      reject(request.error);
    };

    request.onsuccess = () => {
      db = request.result;

      resolve(db);
    };

    request.onupgradeneeded = (event) => {
      const database = event.target.result;

      // Products store - for offline product lookup. Body is encrypted;
      // product_id (the keyPath) stays plaintext since it's used as a key.
      if (!database.objectStoreNames.contains(STORES.PRODUCTS)) {
        database.createObjectStore(STORES.PRODUCTS, { keyPath: 'product_id' });
      }

      // Sales queue - for offline sales that need syncing. Sensitive fields
      // (items/customer/notes) live in the encrypted `enc` blob; status and
      // created_at stay plaintext because they're indexed for queries.
      if (!database.objectStoreNames.contains(STORES.SALES_QUEUE)) {
        const salesStore = database.createObjectStore(STORES.SALES_QUEUE, { keyPath: 'local_id', autoIncrement: true });
        salesStore.createIndex('status', 'status', { unique: false });
        salesStore.createIndex('created_at', 'created_at', { unique: false });
      }

      // Settings store
      if (!database.objectStoreNames.contains(STORES.SETTINGS)) {
        database.createObjectStore(STORES.SETTINGS, { keyPath: 'key' });
      }

      // Sync log - track sync history
      if (!database.objectStoreNames.contains(STORES.SYNC_LOG)) {
        const syncStore = database.createObjectStore(STORES.SYNC_LOG, { keyPath: 'id', autoIncrement: true });
        syncStore.createIndex('timestamp', 'timestamp', { unique: false });
      }

      // Recent customers - for offline customer lookup during checkout.
      // Keyed by phone (the only field we need unencrypted for the key).
      if (!database.objectStoreNames.contains(STORES.RECENT_CUSTOMERS)) {
        database.createObjectStore(STORES.RECENT_CUSTOMERS, { keyPath: 'phone' });
      }

      // Refill eligibility schedule - lets POS flag "no refills left" /
      // "prescription expired" for a known customer while offline, without
      // touching any clinical E2EE ciphertext (plaintext policy metadata
      // only — same fields already used server-side for this check).
      if (!database.objectStoreNames.contains(STORES.REFILL_SCHEDULE)) {
        database.createObjectStore(STORES.REFILL_SCHEDULE, { keyPath: 'phone' });
      }

      // Generic "created while offline, sync when reconnected" queues — new
      // items, new purchases (Direct Receipts) and staff-filed ADR reports.
      // Same shape as the sales queue: encrypted body, plaintext status/date
      // for indexing.
      [STORES.PRODUCT_QUEUE, STORES.PURCHASE_QUEUE, STORES.ADR_QUEUE].forEach((name) => {
        if (!database.objectStoreNames.contains(name)) {
          const store = database.createObjectStore(name, { keyPath: 'local_id', autoIncrement: true });
          store.createIndex('status', 'status', { unique: false });
          store.createIndex('created_at', 'created_at', { unique: false });
        }
      });
    };
  });
};

/**
 * Get database instance
 */
const getDB = async () => {
  if (!db) {
    await initOfflineDB();
  }
  return db;
};

// ==================== PRODUCTS ====================

// Store products, their branch and sync markers atomically. A failed write must
// never leave a new success timestamp or label another branch's data as ready.
export const applyProductSnapshot = async (snapshot, validate) => {
  const now = new Date().toISOString();
  const rows = await Promise.all(snapshot.products.map(async product => ({
    product_id: product.product_id, enc: await encryptValue(product), cached_at: now,
  })));
  const database = await getDB();
  validate();
  const tx = database.transaction([STORES.PRODUCTS, STORES.SETTINGS, STORES.SALES_QUEUE], 'readwrite');
  let blockedError = null;
  const queue = tx.objectStore(STORES.SALES_QUEUE).index('status');
  ['pending', 'failed'].forEach(status => {
    const count = queue.count(status);
    count.onsuccess = () => {
      if (count.result) {
        blockedError = new Error('Pending sales must sync before replacing offline stock.');
        tx.abort();
      }
    };
  });
  const products = tx.objectStore(STORES.PRODUCTS);
  const settings = tx.objectStore(STORES.SETTINGS);
  if (!snapshot.is_delta) products.clear();
  rows.forEach(row => products.put(row));
  (snapshot.deleted_product_ids || []).forEach(id => products.delete(id));
  Object.entries({
    cached_branch_id: snapshot.branch_id, cached_branch_name: snapshot.branch_name,
    last_product_sync: now, last_product_sync_watermark: snapshot.timestamp,
    product_cache_schema: OFFLINE_CACHE_VERSION,
    frequent_products: { branch_id: snapshot.branch_id, ids: snapshot.frequent_product_ids },
  }).forEach(([key, value]) => settings.put({ key, value }));
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve(rows.length);
    tx.onerror = () => reject(blockedError || tx.error);
    tx.onabort = () => reject(blockedError || tx.error || new Error('Product cache update was cancelled'));
  });
};

/**
 * Cache products for offline use (encrypted at rest).
 */
export const cacheProducts = async (products) => {
  const rows = await Promise.all(products.map(async (p) => ({
    product_id: p.product_id,
    enc: await encryptValue(p),
    cached_at: new Date().toISOString(),
  })));

  const database = await getDB();
  const transaction = database.transaction(STORES.PRODUCTS, 'readwrite');
  const store = transaction.objectStore(STORES.PRODUCTS);
  store.clear();
  for (const row of rows) store.put(row);

  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve(products.length);
    transaction.onerror = () => reject(transaction.error);
  });
};

/**
 * Delta-sync variant of `cacheProducts` — upserts only the given products
 * without clearing the rest of the local cache.
 */
export const upsertProducts = async (products) => {
  if (!products?.length) return 0;
  const rows = await Promise.all(products.map(async (p) => ({
    product_id: p.product_id,
    enc: await encryptValue(p),
    cached_at: new Date().toISOString(),
  })));

  const database = await getDB();
  const transaction = database.transaction(STORES.PRODUCTS, 'readwrite');
  const store = transaction.objectStore(STORES.PRODUCTS);
  for (const row of rows) store.put(row);

  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve(rows.length);
    transaction.onerror = () => reject(transaction.error);
  });
};

/**
 * Remove products from the local cache that were deleted server-side
 * since the last delta sync.
 */
export const deleteProducts = async (productIds) => {
  if (!productIds?.length) return 0;
  const database = await getDB();
  const transaction = database.transaction(STORES.PRODUCTS, 'readwrite');
  const store = transaction.objectStore(STORES.PRODUCTS);
  for (const id of productIds) store.delete(id);

  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve(productIds.length);
    transaction.onerror = () => reject(transaction.error);
  });
};

/**
 * Get all cached products (decrypted)
 */
export const getCachedProducts = async () => {
  const database = await getDB();
  const transaction = database.transaction(STORES.PRODUCTS, 'readonly');
  const store = transaction.objectStore(STORES.PRODUCTS);
  const request = store.getAll();

  const rows = await new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result || []);
    request.onerror = () => reject(request.error);
  });

  const decrypted = await Promise.all(rows.map(async (row) => {
    const data = await decryptValue(row.enc);
    return data ? { ...data, product_id: row.product_id, cached_at: row.cached_at } : null;
  }));
  return decrypted.filter(Boolean);
};

/**
 * Get product by ID (decrypted)
 */
export const getCachedProduct = async (productId) => {
  const database = await getDB();
  const transaction = database.transaction(STORES.PRODUCTS, 'readonly');
  const store = transaction.objectStore(STORES.PRODUCTS);
  const request = store.get(productId);

  const row = await new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
  if (!row) return null;
  const data = await decryptValue(row.enc);
  return data ? { ...data, product_id: row.product_id, cached_at: row.cached_at } : null;
};

/**
 * Search cached products
 */
export const searchCachedProducts = async (query) => {
  const products = await getCachedProducts();
  const lowerQuery = query.toLowerCase();

  return products.filter(p =>
    p.name?.toLowerCase().includes(lowerQuery) ||
    p.generic_name?.toLowerCase().includes(lowerQuery) ||
    p.barcode?.includes(query)
  );
};

/**
 * Update local product stock (after offline sale)
 */
export const updateLocalStock = async (productId, quantitySold) => {
  const product = await getCachedProduct(productId);
  if (!product) return true;

  product.stock_quantity = Math.max(0, (product.stock_quantity || 0) - quantitySold);
  product.local_modified = true;
  const enc = await encryptValue(product);
  const cached_at = product.cached_at || new Date().toISOString();

  const database = await getDB();
  const transaction = database.transaction(STORES.PRODUCTS, 'readwrite');
  transaction.objectStore(STORES.PRODUCTS).put({ product_id: productId, enc, cached_at });

  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve(true);
    transaction.onerror = () => reject(transaction.error);
  });
};

// ==================== SALES QUEUE ====================

/**
 * Add sale to offline queue (sensitive fields encrypted)
 */
export const addOfflineSale = async (saleData) => {
  const offline_id = `offline_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  const created_at = new Date().toISOString();
  const enc = await encryptValue({
    items: saleData.items,
    subtotal: saleData.subtotal,
    discount: saleData.discount,
    total: saleData.total,
    payment_method: saleData.payment_method,
    cash_received: saleData.cash_received,
    change_due: saleData.change_due,
    customer_name: saleData.customer_name,
    customer_phone: saleData.customer_phone,
    notes: saleData.notes,
    prescription_info: saleData.prescription_info,
  });

  const database = await getDB();
  const transaction = database.transaction(STORES.SALES_QUEUE, 'readwrite');
  const store = transaction.objectStore(STORES.SALES_QUEUE);
  const request = store.add({ offline_id, status: 'pending', created_at, sync_attempts: 0, enc });

  const localId = await new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });

  // Update local stock for each item (separate transaction on the products store)
  for (const item of saleData.items || []) {
    await updateLocalStock(item.product_id, item.quantity);
  }

  return { ...saleData, offline_id, status: 'pending', created_at, local_id: localId };
};

const decryptQueueRow = async (row) => {
  const data = await decryptValue(row.enc);
  return {
    ...(data || {}),
    local_id: row.local_id,
    offline_id: row.offline_id,
    status: row.status,
    created_at: row.created_at,
    sync_attempts: row.sync_attempts,
    last_error: row.last_error,
    last_attempt_at: row.last_attempt_at,
    server_sale_id: row.server_sale_id,
    synced_at: row.synced_at,
  };
};

/**
 * Unresolved offline sales remain outstanding even after a rejected attempt.
 */
export const getPendingSales = async () => {
  const database = await getDB();
  const transaction = database.transaction(STORES.SALES_QUEUE, 'readonly');
  const store = transaction.objectStore(STORES.SALES_QUEUE);
  const index = store.index('status');
  const batches = await Promise.all(['pending', 'failed'].map(status => {
    const request = index.getAll(status);
    return new Promise((resolve, reject) => {
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });
  }));
  const rows = batches.flat().sort((a, b) => String(a.created_at).localeCompare(String(b.created_at)));
  return Promise.all(rows.map(decryptQueueRow));
};

/**
 * Get all offline sales (for history display)
 */
export const getAllOfflineSales = async () => {
  const database = await getDB();
  const transaction = database.transaction(STORES.SALES_QUEUE, 'readonly');
  const store = transaction.objectStore(STORES.SALES_QUEUE);
  const request = store.getAll();

  const rows = await new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result || []);
    request.onerror = () => reject(request.error);
  });
  return Promise.all(rows.map(decryptQueueRow));
};

/**
 * Mark sale as synced
 */
export const markSaleSynced = async (localId, serverSaleId) => {
  const database = await getDB();
  const transaction = database.transaction(STORES.SALES_QUEUE, 'readwrite');
  const store = transaction.objectStore(STORES.SALES_QUEUE);

  const request = store.get(localId);

  return new Promise((resolve, reject) => {
    request.onsuccess = () => {
      const sale = request.result;
      if (sale) {
        sale.status = 'synced';
        sale.server_sale_id = serverSaleId;
        sale.synced_at = new Date().toISOString();
        store.put(sale);
      }
      resolve(true);
    };
    request.onerror = () => reject(request.error);
  });
};

/**
 * Mark sale sync failed
 */
export const markSaleFailed = async (localId, error) => {
  const database = await getDB();
  const transaction = database.transaction(STORES.SALES_QUEUE, 'readwrite');
  const store = transaction.objectStore(STORES.SALES_QUEUE);

  const request = store.get(localId);

  return new Promise((resolve, reject) => {
    request.onsuccess = () => {
      const sale = request.result;
      if (sale) {
        sale.status = 'failed';
        sale.sync_attempts = (sale.sync_attempts || 0) + 1;
        sale.last_error = error;
        sale.last_attempt_at = new Date().toISOString();
        store.put(sale);
      }
      resolve(true);
    };
    request.onerror = () => reject(request.error);
  });
};

/**
 * Clear synced sales (cleanup)
 */
export const clearSyncedSales = async () => {
  const database = await getDB();
  const transaction = database.transaction(STORES.SALES_QUEUE, 'readwrite');
  const store = transaction.objectStore(STORES.SALES_QUEUE);
  const index = store.index('status');
  const request = index.openCursor('synced');

  return new Promise((resolve, reject) => {
    let deleted = 0;
    request.onsuccess = (event) => {
      const cursor = event.target.result;
      if (cursor) {
        cursor.delete();
        deleted++;
        cursor.continue();
      } else {
        resolve(deleted);
      }
    };
    request.onerror = () => reject(request.error);
  });
};

// ==================== GENERIC "CREATED OFFLINE" QUEUES ====================
// Shared implementation for the product / purchase / ADR-report queues —
// each is "save it locally now, sync automatically once reconnected",
// same pattern as the sales queue above but for records that only get
// *created* (never decrement local stock, so no conflict logic needed).

// Stable id for "create once" semantics — generated client-side so the
// SAME id can be sent with the first (online) attempt and, if that attempt's
// response never arrives, reused when the request is queued instead. The
// server treats a repeat of this id as already done rather than a duplicate.
export const generateOfflineId = () => `offline_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

const addToQueue = async (storeName, data) => {
  const { offline_id: presetId, ...rest } = data;
  const offline_id = presetId || generateOfflineId();
  const created_at = new Date().toISOString();
  const branch_id = getOfflineBranchId();
  const enc = await encryptValue(rest);
  const database = await getDB();
  const transaction = database.transaction(storeName, 'readwrite');
  const request = transaction.objectStore(storeName).add({ offline_id, status: 'pending', created_at, branch_id, sync_attempts: 0, enc });
  const localId = await new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
  return { ...rest, offline_id, status: 'pending', created_at, branch_id, local_id: localId };
};

const decryptQueueRowGeneric = async (row) => {
  const data = await decryptValue(row.enc);
  return {
    ...(data || {}), local_id: row.local_id, offline_id: row.offline_id, status: row.status,
    created_at: row.created_at, branch_id: row.branch_id, sync_attempts: row.sync_attempts, last_error: row.last_error,
    last_attempt_at: row.last_attempt_at, server_id: row.server_id, synced_at: row.synced_at,
  };
};

const getPendingFromQueue = async (storeName) => {
  const database = await getDB();
  const index = database.transaction(storeName, 'readonly').objectStore(storeName).index('status');
  const batches = await Promise.all(['pending', 'failed'].map(status => {
    const request = index.getAll(status);
    return new Promise((resolve, reject) => {
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });
  }));
  const rows = batches.flat().sort((a, b) => String(a.created_at).localeCompare(String(b.created_at)));
  return Promise.all(rows.map(decryptQueueRowGeneric));
};

const getAllFromQueue = async (storeName) => {
  const database = await getDB();
  const request = database.transaction(storeName, 'readonly').objectStore(storeName).getAll();
  const rows = await new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result || []);
    request.onerror = () => reject(request.error);
  });
  return Promise.all(rows.map(decryptQueueRowGeneric));
};

const markQueueItemSynced = async (storeName, localId, serverId) => {
  const database = await getDB();
  const store = database.transaction(storeName, 'readwrite').objectStore(storeName);
  const request = store.get(localId);
  return new Promise((resolve, reject) => {
    request.onsuccess = () => {
      const row = request.result;
      if (row) {
        row.status = 'synced'; row.server_id = serverId; row.synced_at = new Date().toISOString();
        store.put(row);
      }
      resolve(true);
    };
    request.onerror = () => reject(request.error);
  });
};

const markQueueItemFailed = async (storeName, localId, error) => {
  const database = await getDB();
  const store = database.transaction(storeName, 'readwrite').objectStore(storeName);
  const request = store.get(localId);
  return new Promise((resolve, reject) => {
    request.onsuccess = () => {
      const row = request.result;
      if (row) {
        row.status = 'failed'; row.sync_attempts = (row.sync_attempts || 0) + 1;
        row.last_error = error; row.last_attempt_at = new Date().toISOString();
        store.put(row);
      }
      resolve(true);
    };
    request.onerror = () => reject(request.error);
  });
};

const clearSyncedFromQueue = async (storeName) => {
  const database = await getDB();
  const store = database.transaction(storeName, 'readwrite').objectStore(storeName);
  const request = store.index('status').openCursor('synced');
  return new Promise((resolve, reject) => {
    let deleted = 0;
    request.onsuccess = (event) => {
      const cursor = event.target.result;
      if (cursor) { cursor.delete(); deleted++; cursor.continue(); } else resolve(deleted);
    };
    request.onerror = () => reject(request.error);
  });
};

// Manager decided a stuck "failed" row (e.g. a permanent validation error)
// should be dropped rather than keep retrying forever.
const discardFromQueue = async (storeName, localId) => {
  const database = await getDB();
  const store = database.transaction(storeName, 'readwrite').objectStore(storeName);
  return new Promise((resolve, reject) => {
    const request = store.delete(localId);
    request.onsuccess = () => resolve(true);
    request.onerror = () => reject(request.error);
  });
};

// New items queued while offline (from the Inventory "Add Product" form).
export const addOfflineProduct = (data) => addToQueue(STORES.PRODUCT_QUEUE, data);
export const getPendingProducts = () => getPendingFromQueue(STORES.PRODUCT_QUEUE);
export const getAllOfflineProducts = () => getAllFromQueue(STORES.PRODUCT_QUEUE);
export const markProductSynced = (localId, serverId) => markQueueItemSynced(STORES.PRODUCT_QUEUE, localId, serverId);
export const markProductFailed = (localId, error) => markQueueItemFailed(STORES.PRODUCT_QUEUE, localId, error);
export const clearSyncedProducts = () => clearSyncedFromQueue(STORES.PRODUCT_QUEUE);
export const discardOfflineProduct = (localId) => discardFromQueue(STORES.PRODUCT_QUEUE, localId);

// New purchases (Direct Receipts) queued while offline.
export const addOfflinePurchase = (data) => addToQueue(STORES.PURCHASE_QUEUE, data);
export const getPendingPurchases = () => getPendingFromQueue(STORES.PURCHASE_QUEUE);
export const getAllOfflinePurchases = () => getAllFromQueue(STORES.PURCHASE_QUEUE);
export const markPurchaseSynced = (localId, serverId) => markQueueItemSynced(STORES.PURCHASE_QUEUE, localId, serverId);
export const markPurchaseFailed = (localId, error) => markQueueItemFailed(STORES.PURCHASE_QUEUE, localId, error);
export const clearSyncedPurchases = () => clearSyncedFromQueue(STORES.PURCHASE_QUEUE);
export const discardOfflinePurchase = (localId) => discardFromQueue(STORES.PURCHASE_QUEUE, localId);

// Staff-filed ADR reports queued while offline.
export const addOfflineAdrReport = (data) => addToQueue(STORES.ADR_QUEUE, data);
export const getPendingAdrReports = () => getPendingFromQueue(STORES.ADR_QUEUE);
export const getAllOfflineAdrReports = () => getAllFromQueue(STORES.ADR_QUEUE);
export const markAdrReportSynced = (localId, serverId) => markQueueItemSynced(STORES.ADR_QUEUE, localId, serverId);
export const markAdrReportFailed = (localId, error) => markQueueItemFailed(STORES.ADR_QUEUE, localId, error);
export const clearSyncedAdrReports = () => clearSyncedFromQueue(STORES.ADR_QUEUE);
export const discardOfflineAdrReport = (localId) => discardFromQueue(STORES.ADR_QUEUE, localId);

// ==================== RECENT CUSTOMERS ====================

/**
 * Cache recent customers (name + phone) for offline lookup (encrypted at rest)
 */
export const cacheRecentCustomers = async (customers, branchId, validate = () => {}) => {
  const rows = await Promise.all(
    (customers || [])
      .filter((c) => c.phone)
      .map(async (c) => ({ phone: c.phone, enc: await encryptValue(c) }))
  );

  const database = await getDB();
  validate();
  const transaction = database.transaction([STORES.RECENT_CUSTOMERS, STORES.SETTINGS], 'readwrite');
  const store = transaction.objectStore(STORES.RECENT_CUSTOMERS);
  store.clear();
  for (const row of rows) store.put(row);
  if (branchId) transaction.objectStore(STORES.SETTINGS).put({ key: 'cached_customers_branch_id', value: branchId });

  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve(rows.length);
    transaction.onerror = () => reject(transaction.error);
  });
};

/**
 * Get all cached recent customers (decrypted)
 */
export const getCachedRecentCustomers = async () => {
  if (!getOfflineBranchId() || await getSetting('cached_customers_branch_id') !== getOfflineBranchId()) return [];
  const database = await getDB();
  const transaction = database.transaction(STORES.RECENT_CUSTOMERS, 'readonly');
  const store = transaction.objectStore(STORES.RECENT_CUSTOMERS);
  const request = store.getAll();

  const rows = await new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result || []);
    request.onerror = () => reject(request.error);
  });
  const decrypted = await Promise.all(rows.map((r) => decryptValue(r.enc)));
  return decrypted.filter(Boolean);
};

/**
 * Search cached recent customers by name or phone
 */
export const searchCachedCustomers = async (query) => {
  const customers = await getCachedRecentCustomers();
  if (!query) return customers.slice(0, 10);
  const q = query.toLowerCase();
  return customers
    .filter((c) => c.name?.toLowerCase().includes(q) || c.phone?.includes(query))
    .slice(0, 10);
};

// ==================== REFILL ELIGIBILITY SCHEDULE ====================

/**
 * Cache the refill-eligibility snapshot (encrypted at rest) for offline POS
 * dispensing checks — keyed by patient phone.
 */
export const cacheRefillSchedule = async (entries, branchId, validate = () => {}) => {
  const byPhone = new Map();
  for (const e of entries || []) {
    if (!e.patient_phone) continue;
    const list = byPhone.get(e.patient_phone) || [];
    list.push(e);
    byPhone.set(e.patient_phone, list);
  }
  const rows = await Promise.all(
    Array.from(byPhone.entries()).map(async ([phone, list]) => ({ phone, enc: await encryptValue(list) }))
  );

  const database = await getDB();
  validate();
  const transaction = database.transaction([STORES.REFILL_SCHEDULE, STORES.SETTINGS], 'readwrite');
  const store = transaction.objectStore(STORES.REFILL_SCHEDULE);
  store.clear();
  for (const row of rows) store.put(row);
  if (branchId) transaction.objectStore(STORES.SETTINGS).put({ key: 'cached_refills_branch_id', value: branchId });

  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve(rows.length);
    transaction.onerror = () => reject(transaction.error);
  });
};

/**
 * Get the cached refill-eligibility entries for a given phone number.
 * A patient can have more than one active prescription, so this returns a list.
 */
export const getCachedRefillEligibility = async (phone) => {
  if (!phone) return [];
  if (!getOfflineBranchId() || await getSetting('cached_refills_branch_id') !== getOfflineBranchId()) return [];
  const database = await getDB();
  const transaction = database.transaction(STORES.REFILL_SCHEDULE, 'readonly');
  const store = transaction.objectStore(STORES.REFILL_SCHEDULE);
  const request = store.get(phone);

  const row = await new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
  if (!row) return [];
  const data = await decryptValue(row.enc);
  return Array.isArray(data) ? data : [];
};

// ==================== SETTINGS ====================

/**
 * Save setting to offline storage
 */
export const saveSetting = async (key, value) => {
  const database = await getDB();
  const transaction = database.transaction(STORES.SETTINGS, 'readwrite');
  const store = transaction.objectStore(STORES.SETTINGS);
  store.put({ key, value, updated_at: new Date().toISOString() });

  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve(true);
    transaction.onerror = () => reject(transaction.error);
  });
};

/**
 * Get setting from offline storage
 */
export const getSetting = async (key) => {
  const database = await getDB();
  const transaction = database.transaction(STORES.SETTINGS, 'readonly');
  const store = transaction.objectStore(STORES.SETTINGS);
  const request = store.get(key);

  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result?.value);
    request.onerror = () => reject(request.error);
  });
};

// ==================== SYNC LOG ====================

/**
 * Log sync event
 */
export const logSync = async (type, details) => {
  const database = await getDB();
  const transaction = database.transaction(STORES.SYNC_LOG, 'readwrite');
  const store = transaction.objectStore(STORES.SYNC_LOG);

  store.add({
    type,
    details,
    timestamp: new Date().toISOString()
  });

  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve(true);
    transaction.onerror = () => reject(transaction.error);
  });
};

/**
 * Get recent sync logs
 */
export const getRecentSyncLogs = async (limit = 20) => {
  const database = await getDB();
  const transaction = database.transaction(STORES.SYNC_LOG, 'readonly');
  const store = transaction.objectStore(STORES.SYNC_LOG);
  const request = store.getAll();

  return new Promise((resolve, reject) => {
    request.onsuccess = () => {
      const logs = request.result || [];
      resolve(logs.slice(-limit).reverse());
    };
    request.onerror = () => reject(request.error);
  });
};

// ==================== SYNC MANAGER ====================

/**
 * Get offline status summary
 */
export const getOfflineStatus = async () => {
  const branch = getOfflineBranchId();
  const [cachedBranch, schema, complete, error, frequent, pendingSales, customersBranch, refillsBranch,
    pendingProducts, pendingPurchases, pendingAdrReports] = await Promise.all([
    getSetting('cached_branch_id'), getSetting('product_cache_schema'),
    getSetting('last_complete_offline_sync'), getSetting('last_offline_sync_error'),
    getSetting('frequent_products'), getPendingSales(),
    getSetting('cached_customers_branch_id'), getSetting('cached_refills_branch_id'),
    getPendingProducts(), getPendingPurchases(), getPendingAdrReports(),
  ]);
  const matches = !!branch && cachedBranch === branch;
  const cachedProducts = matches ? await getCachedProducts() : [];
  const customers = matches ? await getCachedRecentCustomers() : [];
  let refillCount = 0;
  if (matches && refillsBranch === branch) {
    const database = await getDB();
    const request = database.transaction(STORES.REFILL_SCHEDULE, 'readonly').objectStore(STORES.REFILL_SCHEDULE).getAll();
    const rows = await new Promise((resolve, reject) => {
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });
    const lists = await Promise.all(rows.map(row => decryptValue(row.enc)));
    refillCount = lists.reduce((sum, list) => sum + (Array.isArray(list) ? list.length : 0), 0);
  }
  const prepared = matches && schema === OFFLINE_CACHE_VERSION && complete?.branch_id === branch
    && complete?.version === OFFLINE_CACHE_VERSION && customersBranch === branch && refillsBranch === branch;
  const productIds = new Set(cachedProducts.map(product => product.product_id));
  return {
    branch_id: branch,
    pending_sales: matches ? pendingSales.length : 0,
    pending_products: pendingProducts.length,
    pending_purchases: pendingPurchases.length,
    pending_adr_reports: pendingAdrReports.length,
    cached_products: cachedProducts.length,
    cached_customers: customers.length,
    cached_refill_rules: refillCount,
    cached_frequent_products: matches && frequent?.branch_id === branch
      ? (frequent.ids || []).filter(id => productIds.has(id)).length : 0,
    last_sync: complete?.branch_id === branch ? complete.at : null,
    last_product_sync: matches ? await getSetting('last_product_sync') : null,
    last_error: error?.branch_id === branch ? error.message : null,
    needs_refresh: !!branch && !prepared,
    is_ready: matches && cachedProducts.length > 0,
    is_prepared: prepared && cachedProducts.length > 0 && !(error?.branch_id === branch && error?.message),
  };
};

/**
 * Check if offline mode is available
 */
export const isOfflineModeAvailable = async () => {
  return (await getOfflineStatus()).is_ready;
};

export default {
  initOfflineDB,
  cacheProducts,
  upsertProducts,
  deleteProducts,
  getCachedProducts,
  getCachedProduct,
  searchCachedProducts,
  updateLocalStock,
  addOfflineSale,
  getPendingSales,
  getAllOfflineSales,
  markSaleSynced,
  markSaleFailed,
  clearSyncedSales,
  cacheRecentCustomers,
  getCachedRecentCustomers,
  searchCachedCustomers,
  cacheRefillSchedule,
  getCachedRefillEligibility,
  generateOfflineId,
  addOfflineProduct,
  getPendingProducts,
  getAllOfflineProducts,
  markProductSynced,
  markProductFailed,
  clearSyncedProducts,
  discardOfflineProduct,
  addOfflinePurchase,
  getPendingPurchases,
  getAllOfflinePurchases,
  markPurchaseSynced,
  markPurchaseFailed,
  clearSyncedPurchases,
  discardOfflinePurchase,
  addOfflineAdrReport,
  getPendingAdrReports,
  getAllOfflineAdrReports,
  markAdrReportSynced,
  markAdrReportFailed,
  clearSyncedAdrReports,
  discardOfflineAdrReport,
  saveSetting,
  getSetting,
  logSync,
  getRecentSyncLogs,
  getOfflineStatus,
  isOfflineModeAvailable,
};
